// treeDrag — the PURE structure the ProcessTree drag reads: which rows are
// draggable (leaf steps AND container-block heads), in document order, plus each
// container's descendant set (so a block drag / a multi-drag never anchors onto
// a row it is carrying with it). DOM-free + walk-derived, unit-tested without
// React; the view supplies the row rects at drag time (pointer hit-testing) and
// funnels the drop through moveStep / moveSteps.

import type { NodeId, WalkNode, WalkResult } from "@archlang/engine";

/** A container (block) node kind — its head is a draggable row and it owns a
 *  body/descendants. Terminators + leaf steps are NOT containers. */
const CONTAINER_KINDS = new Set(["each", "if", "switch", "try", "parallel", "do"]);

/** Recurse a walk node's children (all container shapes) → the child node list.
 *  A leaf/terminator has none. */
function childrenOf(n: WalkNode): readonly WalkNode[] {
  switch (n.kind) {
    case "each":
      return n.children;
    case "parallel":
      return n.branches.flatMap((b) => b.children);
    case "if":
      return [...n.branches.flatMap((b) => b.children), ...(n.elseBranch ? n.elseBranch.children : [])];
    case "switch":
      return n.cases.flatMap((b) => b.children);
    case "try":
      return [...n.tryBranch.children, ...n.catches.flatMap((b) => b.children)];
    case "do":
      return n.body ?? [];
    default:
      return [];
  }
}

export interface TreeDragModel {
  /** Every draggable row's nodeId in document (render) order — leaf steps AND
   *  container heads. Terminators are excluded (not relocatable as a step). This
   *  is the axis the before/after drop anchors against. */
  readonly order: readonly NodeId[];
  /** Container nodeId → the set of ALL nodeIds inside its body (leaf steps,
   *  nested container heads, terminators). A block drag excludes these as drop
   *  anchors — you cannot drop a block among its own children. */
  readonly descendants: ReadonlyMap<NodeId, ReadonlySet<NodeId>>;
}

/** Build the draggable order + descendant sets from a walk tree. Mirrors the
 *  tree's own depth-first render order (heads before their bodies). */
export function treeDragModel(walk: WalkResult | null): TreeDragModel {
  const order: NodeId[] = [];
  const descendants = new Map<NodeId, Set<NodeId>>();
  if (!walk) return { order, descendants };

  // Collect every nodeId (any kind) inside a node's subtree — for the
  // descendant sets (a block carries terminators + nested heads too).
  const collectSubtree = (n: WalkNode, into: Set<NodeId>): void => {
    for (const c of childrenOf(n)) {
      if ("nodeId" in c) into.add(c.nodeId);
      collectSubtree(c, into);
    }
  };

  const visit = (nodes: readonly WalkNode[]): void => {
    for (const n of nodes) {
      if (n.kind === "terminator") continue; // not a draggable row
      if (!("nodeId" in n)) continue;
      order.push(n.nodeId);
      if (CONTAINER_KINDS.has(n.kind)) {
        const set = new Set<NodeId>();
        collectSubtree(n, set);
        descendants.set(n.nodeId, set);
      }
      visit(childrenOf(n));
    }
  };
  visit(walk.tree);
  return { order, descendants };
}

/** One draggable tree row as a vertical band in CLIENT space (a DOM rect the
 *  view measured at drag time — pointer hit-testing, not a layout decision). */
export interface TreeRowBand {
  readonly nodeId: NodeId;
  readonly top: number;
  readonly height: number;
}

/** The set of nodeIds a drag is CARRYING — the dragged node itself plus, when it
 *  is a block, its descendants; and for a multi-selection, every selected node
 *  plus each one's descendants. These are excluded as drop anchors (you can't
 *  drop the moving set onto a row inside it). */
export function carriedNodeIds(
  moving: readonly NodeId[],
  model: TreeDragModel,
): ReadonlySet<NodeId> {
  const out = new Set<NodeId>(moving);
  for (const id of moving) {
    const desc = model.descendants.get(id);
    if (desc) for (const d of desc) out.add(d);
  }
  return out;
}
